import { useEffect, useState } from 'react';
import { 
  AlertTriangle, 
  CheckCircle, 
  RefreshCw, 
  Upload,
  Image as ImageIcon,
  Database,
  ChevronLeft,
  ChevronRight,
  Filter,
  RotateCcw,
  HardDrive,
  Link as LinkIcon,
  ShieldCheck,
  Archive
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { api } from '../api/client';

interface ImageItem {
  gameId: string;
  gameName: string;
  type: string;
  index?: number;
  primary: string;
  r2Key?: string;
  r2Size?: number;
  status: string;
  failCount?: number;
  failedAt?: string;
  lastError?: string;
  updatedAt: string;
}

interface ImageStats {
  total: number;
  byStatus: { active: number; failed: number; pending: number };
  bySource: { primaryOnly: number; r2Only: number; both: number };
  byType: { cover: number; icon: number; banner: number; screenshot: number };
  storage: { r2TotalSize: number; r2ObjectCount: number };
}

export function Images() {
  useAuthStore();
  const [stats, setStats] = useState<ImageStats | null>(null);
  const [images, setImages] = useState<ImageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({
    status: 'all',
    type: '',
    hasR2: ''
  });

  const limit = 20;

  useEffect(() => {
    fetchStats();
    fetchImages();
  }, [page, filter]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/images/stats');
      if (res.data?.success) {
        setStats(res.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchImages = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        status: filter.status
      });
      if (filter.type) params.append('type', filter.type);
      if (filter.hasR2) params.append('hasR2', filter.hasR2);

      const res = await api.get(`/admin/images?${params}`);
      if (res.data?.success) {
        setImages(res.data.data);
        setTotal(res.data.meta?.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (gameId?: string, type?: string, verifyAll = false) => {
    setActionLoading('verify');
    try {
      const body = gameId ? { gameId, type } : verifyAll ? { all: true } : { all: true, status: 'failed' };
      const res = await api.post('/admin/images/verify', body);
      if (res.data?.success) {
        const { verified, recovered, stillFailed } = res.data.data;
        alert(`验证完成：共 ${verified} 个图片，${recovered} 个恢复, ${stillFailed} 个仍失效`);
        fetchStats();
        fetchImages();
      }
    } catch (error) {
      alert('验证失败');
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerifyAll = async () => {
    if (!confirm('确定要验证所有图片状态吗？这可能需要一些时间。')) return;
    await handleVerify(undefined, undefined, true);
  };

  const handleBackup = async (gameId?: string, type?: string) => {
    setActionLoading('backup');
    try {
      const body = gameId ? { gameId, type } : { all: true, onlyMissingR2: true };
      const res = await api.post('/admin/images/backup', body);
      if (res.data?.success) {
        alert(`备份完成：${res.data.data.success} 成功, ${res.data.data.failed} 失败`);
        fetchStats();
        fetchImages();
      }
    } catch (error) {
      alert('备份失败');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBackupAllActive = async () => {
    if (!confirm('确定要备份所有状态有效的图片吗？\n\n注意：只有状态为「正常」的图片会被备份，失效的图片将被跳过。')) return;
    setActionLoading('backup-active');
    try {
      const res = await api.post('/admin/images/backup', { all: true, onlyActive: true, onlyMissingR2: true });
      if (res.data?.success) {
        alert(`备份完成：${res.data.data.success} 成功, ${res.data.data.failed} 失败`);
        fetchStats();
        fetchImages();
      }
    } catch (error) {
      alert('备份失败');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCleanup = async (action: string) => {
    if (!confirm('确定执行此操作？')) return;
    
    setActionLoading('cleanup');
    try {
      const res = await api.post('/admin/images/cleanup', { action, confirm: true });
      if (res.data?.success) {
        alert(`清理完成：处理了 ${res.data.data.processed} 个项目`);
        fetchStats();
        fetchImages();
      }
    } catch (error) {
      alert('清理失败');
    } finally {
      setActionLoading(null);
    }
  };

  const formatSize = (bytes?: number) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* 标题 */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-white">图片管理</h1>
        <div className="flex gap-2">
          <button
            onClick={() => fetchImages()}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-lg hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </button>
        </div>
      </div>

      {/* 统计卡片 */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-zinc-900 p-4 rounded-xl border border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-zinc-100 dark:bg-zinc-800 rounded-lg">
                <ImageIcon className="w-5 h-5 text-zinc-600 dark:text-zinc-400" />
              </div>
              <div>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">总图片</p>
                <p className="text-xl font-bold text-zinc-900 dark:text-white">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-zinc-900 p-4 rounded-xl border border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">正常</p>
                <p className="text-xl font-bold text-green-600 dark:text-green-400">{stats.byStatus.active}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-zinc-900 p-4 rounded-xl border border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">失效</p>
                <p className="text-xl font-bold text-red-600 dark:text-red-400">{stats.byStatus.failed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-zinc-900 p-4 rounded-xl border border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <HardDrive className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">R2存储</p>
                <p className="text-xl font-bold text-zinc-900 dark:text-white">{stats.storage.r2ObjectCount}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 快捷操作 */}
      <div className="bg-white dark:bg-zinc-900 p-4 rounded-xl border border-zinc-200 dark:border-zinc-800">
        <h3 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 mb-3">快捷操作</h3>
        <div className="flex flex-wrap gap-2">
          {/* 第一行：验证相关 */}
          <button
            onClick={() => handleVerify()}
            disabled={actionLoading === 'verify'}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${actionLoading === 'verify' ? 'animate-spin' : ''}`} />
            验证失效外链
          </button>
          <button
            onClick={handleVerifyAll}
            disabled={actionLoading === 'verify'}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors disabled:opacity-50"
          >
            <ShieldCheck className={`w-4 h-4 ${actionLoading === 'verify' ? 'animate-spin' : ''}`} />
            验证所有图片状态
          </button>
          {/* 第二行：备份相关 */}
          <button
            onClick={() => handleBackup()}
            disabled={actionLoading === 'backup'}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50"
          >
            <Upload className={`w-4 h-4 ${actionLoading === 'backup' ? 'animate-spin' : ''}`} />
            备份缺失图片
          </button>
          <button
            onClick={handleBackupAllActive}
            disabled={actionLoading === 'backup-active'}
            className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors disabled:opacity-50"
          >
            <Archive className={`w-4 h-4 ${actionLoading === 'backup-active' ? 'animate-spin' : ''}`} />
            备份所有有效图片
          </button>
          {/* 清理相关 */}
          <button
            onClick={() => handleCleanup('resetFailedStatus')}
            disabled={actionLoading === 'cleanup'}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50"
          >
            <RotateCcw className="w-4 h-4" />
            重置失效状态
          </button>
        </div>
      </div>

      {/* 筛选器 */}
      <div className="bg-white dark:bg-zinc-900 p-4 rounded-xl border border-zinc-200 dark:border-zinc-800">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-400" />
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm"
            >
              <option value="all">所有状态</option>
              <option value="active">正常</option>
              <option value="failed">失效</option>
              <option value="pending">待处理</option>
            </select>
          </div>

          <select
            value={filter.type}
            onChange={(e) => setFilter({ ...filter, type: e.target.value })}
            className="px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm"
          >
            <option value="">所有类型</option>
            <option value="cover">封面</option>
            <option value="icon">图标</option>
            <option value="banner">横幅</option>
            <option value="screenshot">截图</option>
          </select>

          <select
            value={filter.hasR2}
            onChange={(e) => setFilter({ ...filter, hasR2: e.target.value })}
            className="px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm"
          >
            <option value="">R2备份</option>
            <option value="true">有</option>
            <option value="false">无</option>
          </select>
        </div>
      </div>

      {/* 图片列表 */}
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 dark:text-zinc-400">
            <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
            加载中...
          </div>
        ) : images.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 dark:text-zinc-400">
            <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
            暂无图片数据
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-zinc-500 dark:text-zinc-400">游戏</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-zinc-500 dark:text-zinc-400">类型</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-zinc-500 dark:text-zinc-400">状态</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-zinc-500 dark:text-zinc-400">外链</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-zinc-500 dark:text-zinc-400">R2</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-zinc-500 dark:text-zinc-400">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                  {images.map((img, idx) => (
                    <tr key={`${img.gameId}-${img.type}-${idx}`} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/30">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-zinc-900 dark:text-white">{img.gameName}</p>
                          <p className="text-xs text-zinc-500 dark:text-zinc-400">{img.gameId}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 text-xs rounded">
                          {img.type === 'cover' && '封面'}
                          {img.type === 'icon' && '图标'}
                          {img.type === 'banner' && '横幅'}
                          {img.type === 'screenshot' && `截图${img.index !== undefined ? `#${img.index + 1}` : ''}`}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {img.status === 'active' ? (
                          <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                            <CheckCircle className="w-4 h-4" />
                            正常
                          </span>
                        ) : img.status === 'failed' ? (
                          <span className="flex items-center gap-1 text-red-600 dark:text-red-400 text-sm">
                            <AlertTriangle className="w-4 h-4" />
                            失效
                            {img.failCount && img.failCount > 1 && ` (${img.failCount}次)`}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-zinc-500 dark:text-zinc-400 text-sm">
                            <RefreshCw className="w-4 h-4" />
                            待处理
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {img.primary ? (
                          <a 
                            href={img.primary} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            <LinkIcon className="w-3 h-3" />
                            查看
                          </a>
                        ) : (
                          <span className="text-sm text-zinc-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {img.r2Key ? (
                          <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                            <Database className="w-3 h-3" />
                            {formatSize(img.r2Size)}
                          </span>
                        ) : (
                          <span className="text-sm text-zinc-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {img.status === 'failed' && (
                            <button
                              onClick={() => handleBackup(img.gameId, img.type)}
                              disabled={actionLoading === 'backup'}
                              className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                              title="备份"
                            >
                              <Upload className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleVerify(img.gameId, img.type)}
                            disabled={actionLoading === 'verify'}
                            className="p-1.5 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded"
                            title="验证"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 分页 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-200 dark:border-zinc-800">
                <p className="text-sm text-zinc-500 dark:text-zinc-400">
                  共 {total} 条，第 {page}/{totalPages} 页
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 border border-zinc-200 dark:border-zinc-700 rounded-lg disabled:opacity-50 hover:bg-zinc-50 dark:hover:bg-zinc-800"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 border border-zinc-200 dark:border-zinc-700 rounded-lg disabled:opacity-50 hover:bg-zinc-50 dark:hover:bg-zinc-800"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}